/**
 * useDocumentPresence — viewing-presence heartbeat for the review panel.
 *
 * While a real document is open, POSTs /documents/{id}/presence every 15s (the backend
 * treats a viewer as live for 45s — 3 missed beats) and reports how many OTHER users
 * currently have the document open. Purely informational: it never locks, blocks or
 * gates anything, and closing the tab needs no explicit release — the server-side TTL
 * ages the viewer out on its own.
 */
import { useEffect, useRef, useState } from "react";
import { ApiException } from "@/services/api";
import { useDocumentsApi } from "@/services/api/documents-api";

const HEARTBEAT_INTERVAL_MS = 15_000;

export function useDocumentPresence(documentId: number | null | undefined): number {
    const documentsApi = useDocumentsApi();
    const [otherViewerCount, setOtherViewerCount] = useState(0);
    // A 404 with no structured body means the backend predates the presence endpoint —
    // stop heartbeating for the rest of the session instead of spamming a dead route.
    const isUnsupportedRef = useRef(false);

    useEffect(() => {
        setOtherViewerCount(0);
        // Placeholder/synthetic documents have non-positive ids — nothing to report on.
        if (!documentId || documentId <= 0 || isUnsupportedRef.current) return;

        let cancelled = false;
        let intervalId: number | null = null;

        const beat = async () => {
            try {
                const res = await documentsApi.heartbeatPresence(documentId);
                if (!cancelled) setOtherViewerCount(res.otherViewerCount);
            } catch (err) {
                // Presence is best-effort — errors are never surfaced to the user.
                if (err instanceof ApiException && err.status === 404 && !err.errorResponse) {
                    isUnsupportedRef.current = true;
                    if (intervalId != null) window.clearInterval(intervalId);
                }
                if (!cancelled) setOtherViewerCount(0);
            }
        };

        void beat();
        intervalId = window.setInterval(() => void beat(), HEARTBEAT_INTERVAL_MS);

        return () => {
            cancelled = true;
            if (intervalId != null) window.clearInterval(intervalId);
        };
    }, [documentId, documentsApi]);

    return otherViewerCount;
}
